import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import useReportsSearchParamsConfig from '~/routes/app/reports/hooks/useReportsSearchParamsConfig';
import useFeatures from './useFeatures';
import useSearchParamsConfig from './useSearchParamsConfig';
import useStable from './useStable';

const defaultOptions = {
  keys: [
    'createdPeriod',
    'modifiedPeriod',
    'timeApprovalStatuses',
    'timeClientApprovalStatuses',
    'timeBillableTypes',
    'timeInvoiced',
    'timeOffTypes',
  ],
  defaults: null,
};

/**
 *
 * @param {keys, defaults} options
 * @returns
 */
export default function useTimeFilters(options = {}) {
  options = useStable({ ...defaultOptions, ...options });

  const features = useFeatures();

  const filters = useMemo(
    () =>
      _.pick(
        {
          createdPeriod: null,
          modifiedPeriod: null,
          timeApprovalStatuses: [],
          timeClientApprovalStatuses: [],
          timeBillableTypes: [],
          timeInvoiced: null,
          timeOffTypes: [],
        },
        options.keys,
      ),
    [options.keys],
  );

  const [defaultParams] = useState(options.defaults);

  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();

  const timeSearchParamsConfig = useMemo(
    () =>
      _.pick(
        {
          createdPeriod: reportsSearchParamsConfig.createdPeriod,
          modifiedPeriod: reportsSearchParamsConfig.modifiedPeriod,
          timeBillableTypes: searchParamsConfig.timeBillableTypes,
          timeOffTypes: searchParamsConfig.timeOffTypes,
          timeApprovalStatuses: searchParamsConfig.timeApprovalStatuses,
          timeClientApprovalStatuses: searchParamsConfig.timeClientApprovalStatuses,
          timeInvoiced: searchParamsConfig.timeInvoiced,
          ...defaultParams,
        },
        options.keys,
      ),
    [searchParamsConfig, reportsSearchParamsConfig, defaultParams, options.keys],
  );

  const mapUrlSearchParams = useCallback(
    (params) => ({
      createdStart: params.createdPeriod?.start ?? undefined,
      createdEnd: params.createdPeriod?.end ?? undefined,
      modifiedStart: params.modifiedPeriod?.start ?? undefined,
      modifiedEnd: params.modifiedPeriod?.end ?? undefined,
      timeBillableTypeId: params.timeBillableTypes?.map((v) => v.id),
      timeOffTypeId: params.timeOffTypes?.map((v) => v.id),
      timeApprovalStatusId: params.timeApprovalStatuses?.map((v) => v.id),
      timeClientApprovalStatusId: params.timeClientApprovalStatuses?.map((v) => v.id),
      timeInvoiced: params.timeInvoiced ?? undefined,
    }),
    [],
  );

  const mapClickThroughParams = useCallback(
    (filters) => ({
      createdPeriod: filters.createdPeriod ?? undefined,
      modifiedPeriod: filters.modifiedPeriod ?? undefined,
      timeBillableTypeId: filters.timeBillableTypes?.map((v) => v.id),
      timeOffTypeId: filters.timeOffTypes?.map((v) => v.id),
      timeApprovalStatusId: filters.timeApprovalStatuses?.map((v) => v.id),
      timeClientApprovalStatusId: features.clientApprovals
        ? filters.timeClientApprovalStatuses?.map((v) => v.id)
        : undefined,
      timeInvoiced: filters.timeInvoiced ?? undefined,
    }),
    [features],
  );

  return useMemo(
    () => ({ filters, searchParamsConfig: timeSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams }),
    [filters, timeSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams],
  );
}
